import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMasterData } from "../context/MasterDataContext";
import { getUserAction } from "../helpers/pageAccess";
import { COMMAND_ACTIONS } from "./actionRegistry";
import { CommandItem } from "./types";

const RECENTS_KEY = "cmd_palette_recent";
const MAX_RECENTS = 5;
const MAX_RESULTS = 8;

const readRecents = (): string[] => {
	try {
		const raw = localStorage.getItem(RECENTS_KEY);
		const arr = raw ? JSON.parse(raw) : [];
		return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
	} catch {
		return [];
	}
};

const isMac =
	typeof navigator !== "undefined" &&
	/mac/i.test(navigator.platform || navigator.userAgent || "");

// Always-visible header search. Type to find any page you can access, or a
// registered deep action; Enter/click navigates there. Ctrl/Cmd+K focuses it.
const CommandSearch = () => {
	const navigate = useNavigate();
	const { masterData } = useMasterData();

	const [query, setQuery] = useState("");
	const [open, setOpen] = useState(false);
	const [highlight, setHighlight] = useState(0);
	const [recents, setRecents] = useState<string[]>(readRecents);

	const inputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// ── Build the searchable index: pages (auto-derived) + registered actions ──
	const index = useMemo<CommandItem[]>(() => {
		if (!masterData.isPermissionLoaded) return [];

		const seen = new Set<string>();
		const pages: CommandItem[] = masterData.actionList
			.filter((e) => e.path && e.title && e.permission?.includes("view"))
			.filter((e) => (seen.has(e.path) ? false : (seen.add(e.path), true)))
			.map((e) => {
				const parent = masterData.permissionList.find(
					(p) => p.module_code === e.main_module_code
				);
				const breadcrumb =
					parent && parent.title && parent.module_code !== e.module_code
						? parent.title
						: "";
				return {
					type: "page",
					id: `page:${e.path}`,
					label: e.title,
					breadcrumb,
					path: e.path,
					keywords: [e.title, e.module_code, e.main_module_code ?? ""].filter(
						Boolean
					),
					icon: parent?.icon || "bi bi-file-earmark-text",
				};
			});

		const actions: CommandItem[] = COMMAND_ACTIONS.filter((a) => {
			const perm = getUserAction(
				masterData.actionList,
				a.main_module_code,
				a.module_code
			);
			return perm[a.required_verb];
		}).map((a) => ({
			type: "action",
			id: `action:${a.id}`,
			label: a.label,
			breadcrumb: a.breadcrumb,
			path: a.action_param
				? `${a.navigate_to}?action=${a.action_param}`
				: a.navigate_to,
			keywords: a.keywords,
			icon: a.icon,
		}));

		return [...actions, ...pages];
	}, [masterData.actionList, masterData.permissionList, masterData.isPermissionLoaded]);

	const byId = useMemo(() => {
		const m = new Map<string, CommandItem>();
		index.forEach((it) => m.set(it.id, it));
		return m;
	}, [index]);

	// ── Results: recents when query is empty, ranked matches otherwise ──
	const results = useMemo<CommandItem[]>(() => {
		const q = query.trim().toLowerCase();
		if (!q) {
			return recents
				.map((id) => byId.get(id))
				.filter((x): x is CommandItem => !!x)
				.slice(0, MAX_RECENTS);
		}
		return index
			.map((it) => {
				const hay = [it.label, ...it.keywords].join(" ").toLowerCase();
				const pos = hay.indexOf(q);
				if (pos < 0) return null;
				const prefix = it.label.toLowerCase().startsWith(q) ? 0 : 1;
				return { it, score: prefix * 1000 + pos };
			})
			.filter((x): x is { it: CommandItem; score: number } => !!x)
			.sort((a, b) => a.score - b.score)
			.slice(0, MAX_RESULTS)
			.map((x) => x.it);
	}, [query, index, recents, byId]);

	const hasQuery = query.trim().length > 0;
	const actionResults = results.filter((r) => r.type === "action");
	const pageResults = results.filter((r) => r.type === "page");
	const flat = [...actionResults, ...pageResults];

	useEffect(() => setHighlight(0), [query, open]);

	// ── Ctrl/Cmd+K focuses the search from anywhere ──
	// Capture phase + e.code so we beat the browser's Ctrl+K omnibox shortcut on
	// Windows (a bubble-phase listener can lose the race / be swallowed upstream),
	// and so it works regardless of keyboard layout.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const isK = e.code === "KeyK" || e.key === "k" || e.key === "K";
			if ((e.metaKey || e.ctrlKey) && isK) {
				e.preventDefault();
				e.stopPropagation();
				inputRef.current?.focus();
				setOpen(true);
			}
		};
		window.addEventListener("keydown", onKey, true);
		return () => window.removeEventListener("keydown", onKey, true);
	}, []);

	// ── Outside click closes the dropdown ──
	useEffect(() => {
		const onDown = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, []);

	const select = useCallback(
		(item?: CommandItem) => {
			if (!item) return;
			const next = [item.id, ...recents.filter((id) => id !== item.id)].slice(
				0,
				MAX_RECENTS
			);
			setRecents(next);
			try {
				localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
			} catch {
				/* ignore quota / private-mode errors */
			}
			setQuery("");
			setOpen(false);
			inputRef.current?.blur();
			navigate(item.path);
		},
		[navigate, recents]
	);

	const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setOpen(true);
			setHighlight((h) => Math.min(h + 1, flat.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlight((h) => Math.max(h - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			select(flat[highlight]);
		} else if (e.key === "Escape") {
			setOpen(false);
			inputRef.current?.blur();
		}
	};

	const renderRow = (item: CommandItem, idx: number) => {
		const active = idx === highlight;
		return (
			<div
				key={item.id}
				className={`d-flex align-items-center gap-3 px-4 py-2 ${
					active ? "bg-light-primary" : ""
				}`}
				style={{ cursor: "pointer" }}
				onMouseEnter={() => setHighlight(idx)}
				onMouseDown={(e) => {
					e.preventDefault();
					select(item);
				}}
			>
				<i className={`${item.icon} fs-5 text-muted`} />
				<div className="flex-grow-1 min-w-0">
					<div className="fw-semibold text-dark text-truncate">{item.label}</div>
					{item.breadcrumb && (
						<div className="text-muted" style={{ fontSize: "0.72rem" }}>
							{item.breadcrumb}
						</div>
					)}
				</div>
				{item.type === "action" && (
					<span className="badge badge-light-primary flex-shrink-0">การดำเนินการ</span>
				)}
			</div>
		);
	};

	const groupHeader = (text: string) => (
		<div
			className="px-4 pt-3 pb-1 text-muted fw-semibold text-uppercase"
			style={{ fontSize: "0.7rem", letterSpacing: "0.04em" }}
		>
			{text}
		</div>
	);

	return (
		<div
			className="position-relative flex-grow-1"
			ref={containerRef}
			style={{ width: "clamp(280px, 34vw, 520px)", maxWidth: "100%" }}
		>
			<div className="position-relative">
				<i
					className="bi bi-search position-absolute text-muted"
					style={{
						left: "12px",
						top: "50%",
						transform: "translateY(-50%)",
						fontSize: "0.9rem",
					}}
				/>
				<input
					ref={inputRef}
					type="text"
					className="form-control form-control-sm form-control-solid"
					style={{ paddingLeft: "2.2rem", paddingRight: "2.6rem" }}
					placeholder="ค้นหาเมนู / สิ่งที่ต้องทำ..."
					value={query}
					onChange={(e) => {
						setQuery(e.target.value);
						setOpen(true);
					}}
					onFocus={() => setOpen(true)}
					onKeyDown={onInputKeyDown}
				/>
				<span
					className="position-absolute text-muted d-none d-lg-block"
					style={{
						right: "10px",
						top: "50%",
						transform: "translateY(-50%)",
						fontSize: "0.68rem",
						pointerEvents: "none",
					}}
				>
					{isMac ? "⌘K" : "Ctrl K"}
				</span>
			</div>

			{open && (
				<div
					className="card shadow"
					style={{
						position: "absolute",
						left: 0,
						right: 0,
						top: "calc(100% + 6px)",
						zIndex: 1050,
						border: "1px solid var(--bs-border-color)",
						minWidth: "320px",
					}}
				>
					<div
						className="card-body p-0"
						style={{
							maxHeight: "420px",
							overflowY: "auto",
							overscrollBehavior: "contain",
						}}
					>
						{!masterData.isPermissionLoaded ? (
							<div className="text-center text-muted py-6 fs-7">กำลังโหลด...</div>
						) : flat.length === 0 ? (
							<div className="text-center text-muted py-6 fs-7">
								{hasQuery ? "ไม่พบผลลัพธ์" : "พิมพ์เพื่อค้นหาเมนูหรือสิ่งที่ต้องทำ"}
							</div>
						) : (
							<>
								{!hasQuery && groupHeader("เข้าชมล่าสุด")}
								{hasQuery && actionResults.length > 0 && groupHeader("การดำเนินการ")}
								{actionResults.map((it, i) => renderRow(it, i))}
								{hasQuery && pageResults.length > 0 && groupHeader("หน้า")}
								{pageResults.map((it, i) => renderRow(it, actionResults.length + i))}
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export { CommandSearch };
